import * as React from "react";
import {
    ProcessStage,
    ProcessStageArrow,
    ProcessStageArrowDirection,
    ProcessStageOrder,
    useProcessController
} from "../../Processes";
import {VeBoxCard} from "../../../components/card/VeBoxCard";
import {InputAddon, Spinner, TextInput} from "@vebox/etui-core";
import {VeboxUser} from "../../../providers/VeboxUser";
import {VeboxModels} from "../../../types/veboxModels";
import {VeboxForm} from "../../../components/form/VeboxForm";
import {INPUT_BORDER_BOX, Styles} from "../../../components/common/etui_components_styles";
import {VeboxTreeView} from "../../../components/tree_view/VeboxTreeView";
import {Display} from "../../../components/Display";
import {FormCardFooter} from "../../common";
import {last, UserCreationState, ValueToUpload} from "../UserCreation";
import {AppTranslations} from "../../../components/Utils/translations/AppTranslations";
import {
    changeSelectedCommercialHierarchies,
    changeSelectedHierarchy,
    getAllSelectedNodes,
    getHierarchies,
    HierarchiesSection,
    hierarchyIncludesWord,
    hierarchyToFilteredVeboxCheckboxTree,
    hierarchyToFilteredVeboxTreeItem,
    IconInfoLegend,
    VisibilityIcon
} from "../../../users/HierarchyVisibilityUtils";
import {VISIBILITY_TREE_ICONS} from "../../../users/UserManagement";

declare var AppTranslations: AppTranslations;

const INPUT_BORDER_BOX_WITH_MARGIN: Styles = {
    ...INPUT_BORDER_BOX,
    root: {
        mb: 10,
    },
};

const VISIBILITY_STEP_NUMBER = 2;

export const commercialHierarchyGUID = "d58245f0-5294-11e8-a5e0-525400c5f801"

interface VisibilityStageProps {
    creationState: UserCreationState;
}

export const VisibilityStage: React.FC<VisibilityStageProps> = ({creationState}) => {

    const processController = useProcessController();

    const [hierarchiesIsLoaded, setHierarchiesIsLoaded] = React.useState(false);

    const user = VeboxUser.useCurrentUser();

    const [hierarchiesSection, setHierarchiesSection] = VeboxForm.useFormValueState<HierarchiesSection>(
        ValueToUpload.HIERARCHIES);

    const [hierarchies, setHierarchies] = React.useState<VeboxModels.HierarchiesTree[]>([]);

    const [searchWord, setSearchWord] = React.useState("");
    const [hasFullVisibility, setHasFullVisibility] = React.useState(false);

    const formData = VeboxForm.useFormData();

    const resetValues = () => {
        clearSearchWord();
        setHierarchiesSection({selectedHierarchies: [], selectedCommercialHierarchies: []});
        formData.bloc.set("newsletter", false)
        formData.bloc.set("push_notification", false)
        formData.bloc.set("invisible_user", false)
    };

    const goToNextStep = () => {
        processController.goTo(processController.position + 1);
    };

    const clearSearchWord = () => setSearchWord("");

    const changeSearchWord = (e: React.ChangeEvent<HTMLInputElement>) => setSearchWord(e.target.value);

    const changeHierarchy = (v) => {
        setHierarchiesSection(changeSelectedHierarchy(v, hierarchiesSection));
    };

    const changeCommercialHierarchies = (v) => {
        setHierarchiesSection(changeSelectedCommercialHierarchies(v, hierarchiesSection));
    };

    React.useEffect(() => {
        if (user) {
            getHierarchies()
                .then((x) => {
                    setHierarchies(x);
                    setHierarchiesIsLoaded(true);
                    if (x.length === 0) {
                        setHierarchiesSection({
                            selectedHierarchies: "all_companies" as any,
                            selectedCommercialHierarchies: [],
                        })
                    }
                });
        }
    }, [user]);

    React.useEffect(() => {
        setHasFullVisibility(hierarchiesSection?.selectedHierarchies === "all_companies");
    }, [hierarchiesSection]);

    React.useEffect(() => {
        switch (creationState) {
            case UserCreationState.INITIAL: {
                setSearchWord("");
                break;
            }
        }
    }, [creationState]);

    const commercialHierarchy = hierarchies.find(h => h.hierarchyGuid === commercialHierarchyGUID);

    const canCreateInvisibleUser = user?.rolesAndPermissions?.findIndex(r => r.code === 'ROLE_ADMIN') > -1;

    const selectedCommercialNodes = getAllSelectedNodes(hierarchiesSection?.selectedCommercialHierarchies ?? [])

    return (
        <ProcessStage
            stepNumber={VISIBILITY_STEP_NUMBER}
            title={AppTranslations.userCreationProcess.visibilityStageTitle}
            stageOrder={ProcessStageOrder.MIDDLE}
        >
            <div className={"flx-col flx-align-center"}>
                <div className={"flx-row"}>
                    <ProcessStageArrow
                        stepNumber={VISIBILITY_STEP_NUMBER}
                        direction={ProcessStageArrowDirection.BACKWARD}
                    />
                    <VeBoxCard className={"form-card flx-col"} style={{height: "515px"}}>
                        <Display isVisible={!hierarchiesIsLoaded}>
                            <div className={"centered"} style={{width: "100%", height: "420px"}}>
                                <Spinner/>
                            </div>
                        </Display>
                        <Display isVisible={hierarchiesIsLoaded}>
                            <div className={"flx-row"}>
                                <TextInput
                                    label={AppTranslations.filters.filterBy}
                                    styles={{...INPUT_BORDER_BOX_WITH_MARGIN, root: {width: "100%"}} as any}
                                    autoComplete={"off"}
                                    value={searchWord}
                                    onChange={changeSearchWord}
                                    endInputAddon={
                                        <InputAddon position={"end"}>
                                            {Boolean(searchWord) && <span
                                                className="material-icons-outlined"
                                                onClick={clearSearchWord}
                                            >clear</span>}
                                        </InputAddon>
                                    }
                                />
                                <div className={"visibility-legend-icon-info"}>
                                    <IconInfoLegend showWarningIcon={false}/>
                                </div>
                            </div>
                            <VeBoxCard
                                style={{
                                    overflow: "auto",
                                    height: 300,
                                    margin: 0,
                                    marginBottom: 10,
                                    padding: 10
                                }}
                            >
                                <VeboxForm.Section name={"hierarchies"}>
                                    <VeboxTreeView.RadioGroup
                                        className={"hierarchy-tree"}
                                        value={hierarchiesSection?.selectedHierarchies ?? []}
                                        onChange={changeHierarchy}
                                    >
                                        {(AppTranslations.userCreationProcess.allCompany).toLowerCase()
                                            .includes(searchWord.toLowerCase()) && hierarchiesIsLoaded
                                            ? <VeboxTreeView.Radio
                                                label={
                                                    <>
                                                        <Display isVisible={hasFullVisibility}>
                                                            <VisibilityIcon iconName={VISIBILITY_TREE_ICONS.placed}/>
                                                        </Display>
                                                        <div className={"font-weight-400 hierarchy-leaf"}>
                                                            {AppTranslations.userCreationProcess.allCompany}
                                                        </div>
                                                    </>
                                                }
                                                value={"all_companies"}
                                            />
                                            : undefined
                                        }
                                        {
                                            hierarchies
                                                .filter(h => h.hierarchyGuid !== commercialHierarchyGUID)
                                                .map(hierarchyToFilteredVeboxTreeItem(searchWord.toLowerCase(),
                                                    last((hierarchiesSection?.selectedHierarchies as string[]) ?? []),
                                                    hasFullVisibility,
                                                    false))
                                                .filter(c => c !== null && c !== undefined)
                                        }
                                        {
                                            commercialHierarchy
                                            && (commercialHierarchy.children ?? []).filter(
                                                hierarchyIncludesWord(searchWord)).length > 0
                                            &&
                                            (
                                                <VeboxTreeView.Radio
                                                    key={commercialHierarchy?.hierarchyGuid}
                                                    value={commercialHierarchy?.hierarchyGuid}
                                                    label={
                                                        // tslint:disable-next-line:jsx-no-lambda
                                                        (checked) => (
                                                            <>
                                                                <Display isVisible={hasFullVisibility}>
                                                                    <VisibilityIcon iconName={VISIBILITY_TREE_ICONS.visibility}/>
                                                                </Display>
                                                                <div className={"font-weight-400 hierarchy-leaf " + (checked ? "bold" : "")}>
                                                                    {commercialHierarchy?.label}
                                                                </div>
                                                            </>
                                                        )
                                                    }
                                                    expanded={((commercialHierarchy.label.includes(searchWord)
                                                            || (commercialHierarchy.children ?? []).filter(
                                                                hierarchyIncludesWord(searchWord)).length > 0))
                                                        && searchWord !== ""
                                                    }
                                                >
                                                    <VeboxTreeView.CheckboxGroup
                                                        values={hierarchiesSection?.selectedCommercialHierarchies ?? []}
                                                        onChange={changeCommercialHierarchies}
                                                    >
                                                        {(commercialHierarchy?.children ?? []).map(
                                                            hierarchyToFilteredVeboxCheckboxTree(searchWord,
                                                                selectedCommercialNodes,
                                                                false,
                                                                hasFullVisibility))}
                                                    </VeboxTreeView.CheckboxGroup>
                                                </VeboxTreeView.Radio>
                                            )
                                        }
                                    </VeboxTreeView.RadioGroup>
                                </VeboxForm.Section>
                            </VeBoxCard>
                            <div className={"under-overlay"}>
                                <Display isVisible={canCreateInvisibleUser}>
                                    <div className={"font-weight-400"}>
                                        <VeboxForm.Checkbox
                                            name={"invisible_user"}
                                        /> {AppTranslations.userCreationProcess.invisibleUser}
                                    </div>
                                </Display>
                                <div className={"font-weight-400"}>
                                    <VeboxForm.Checkbox name={"push_notification"}/> {AppTranslations.userCreationProcess.pushNotifications}
                                </div>
                                <div className={"font-weight-400"}>
                                    <VeboxForm.Checkbox name={"newsletter"}/> {AppTranslations.userCreationProcess.subscribeToNewsletter}
                                </div>
                            </div>
                            <FormCardFooter
                                onReset={resetValues}
                                onNext={goToNextStep}
                                disabled={(hierarchiesSection?.selectedHierarchies ?? []).length === 0}
                            />
                        </Display>
                    </VeBoxCard>
                    <ProcessStageArrow
                        stepNumber={VISIBILITY_STEP_NUMBER}
                        direction={ProcessStageArrowDirection.FORWARD}
                    />
                </div>
            </div>
        </ProcessStage>
    );
}